import { Badge, Title, Tooltip, UnstyledButton } from '@mantine/core';
import {
  IconAward,
  IconBolt,
  IconBrandAmazon,
  IconBrandFacebook,
  IconBrandGoogle,
  IconBrandMeta,
  IconBrandTiktok,
  IconBrandWhatsapp,
  IconBrowserPlus,
  IconBuildingStore,
  IconCalendar,
  IconCalendarPlus,
  IconChartPie,
  IconDatabaseCog,
  IconDeviceMobile,
  IconFileCode,
  IconFileExport,
  IconFlask,
  IconFolder,
  IconGift,
  IconGitPullRequest,
  IconHeartPlus,
  IconLayersLinked,
  IconLayoutGrid,
  IconMail,
  IconMessage,
  IconMessageCircle,
  IconMouse,
  IconPackages,
  IconPhone,
  IconPhoto,
  IconPlugConnected,
  IconPlus,
  IconQuestionMark,
  IconRefresh,
  IconRobot,
  IconSearch,
  IconSend,
  IconSettings,
  IconSitemap,
  IconSparkles,
  IconStar,
  IconTarget,
  IconTicket,
  IconTrendingUp,
  IconUsers,
  IconUsersGroup,
  IconWebhook,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import useFetch from '../helpers/useFetch';
import classes from './AppSidebar.module.css';
import LockableLinkContainer from './Lockable/LockableLinkContainer';

// Main navigation sections with icons
const mainSections = [
  { id: 'analytics', icon: IconChartPie, label: 'Analytics', dataTour: 'analyticsLink' },
  { id: 'aiHub', icon: IconSparkles, label: 'AI Hub' },
  { id: 'agentic', icon: IconRobot, label: 'Agentic' },
  { id: 'personalization', icon: IconTarget, label: 'Personalization', dataTour: 'webPersonalizationLink' },
  { id: 'campaigns', icon: IconSend, label: 'Campaigns', dataTour: 'campaignLink' },
  { id: 'workflows', icon: IconSitemap, label: 'Workflows', dataTour: 'workFlowsLink' },
  { id: 'channels', icon: IconMail, label: 'Marketing Channels', dataTour: 'marketingChannelsLink' },
  { id: 'data', icon: IconDatabaseCog, label: 'Data Platform', dataTour: 'dataPlatformLink' },
  { id: 'assets', icon: IconPhoto, label: 'Assets', dataTour: 'assetsLink' },
  { id: 'settings', icon: IconSettings, label: 'Settings', dataTour: 'settingsLink' },
];

// Links for each section
const sectionLinks = {
  analytics: [
    {
      name: 'Dashboard',
      icon: IconChartPie,
      to: '/analytics',
      restrictionKey: 'analyticsList',
      restrictedNavigation: true,
      dataTour: 'dashboardLink',
    },
    {
      name: 'Customer Insights',
      icon: IconUsersGroup,
      to: '/customerInsights',
      restrictionKey: 'analyticsList',
      restrictedNavigation: true,
    },
    { name: 'Exports', icon: IconFileExport, to: '/reports', restrictionKey: 'reportsList', dataTour: 'reportsLink' },
    { name: 'A/B Tests', icon: IconFlask, to: '/abTests', restrictionKey: 'abTestsList', restrictedNavigation: true },
    {
      name: 'Product Trends',
      icon: IconTrendingUp,
      to: '/productTrends',
      restrictionKey: 'productTrendsList',
      restrictedNavigation: true,
    },
  ],
  aiHub: [
    {
      name: 'Create',
      icon: IconPlus,
      to: '/generatedLanding',
      restrictionKey: 'createContent',
      restrictedNavigation: true,
    },
    {
      name: 'Browse',
      icon: IconFolder,
      to: '/generatedContent',
      restrictionKey: 'generatedContentList',
      restrictedNavigation: true,
    },
    {
      name: 'Brand Identity',
      icon: IconLayoutGrid,
      // Not plan-locked: every new domain confirms its brand through the Setup Guide.
      to: '/brandContext',
    },
  ],
  agentic: [
    { name: 'Agents', icon: IconRobot, to: '/agents' },
    { name: 'Sales Agent', icon: IconMessageCircle, to: '/chatBot' },
  ],
  personalization: [
    {
      name: 'Product Blocks',
      icon: IconAward,
      to: '/pages?type=recommenders',
      restrictionKey: 'productBlocksList',
      dataTour: 'recommenderLink',
    },
    {
      name: 'Banner Blocks',
      icon: IconBrowserPlus,
      to: '/pages?type=bannerBlocks',
      restrictionKey: 'bannerBlocksList',
      dataTour: 'bannerBlocksLink',
    },
    { name: 'Stories', icon: IconPhoto, to: '/stories', restrictionKey: 'storiesList' },
    { name: 'Search As You Type', icon: IconSearch, to: '/searchAsYouType' },
  ],
  campaigns: [
    {
      name: 'One Off',
      icon: IconCalendarPlus,
      to: '/campaigns?mode=oneOff',
      restrictionKey: 'oneOffCampaignsList',
      dataTour: 'onOffLink',
    },
    {
      name: 'Continuous',
      icon: IconCalendar,
      to: '/campaigns?mode=continuous',
      restrictionKey: 'continousCampaignsList',
    },
    {
      name: 'AI proposals',
      icon: IconGitPullRequest,
      to: '/campaignProposals',
      pendingCountPaths: ['aiDrafts/count?type=simple'],
    },
  ],
  workflows: [
    { name: 'One Off', icon: IconCalendarPlus, to: '/workflows?mode=oneOff', restrictionKey: 'oneOffWorkflowsList' },
    {
      name: 'Continuous',
      icon: IconCalendar,
      to: '/workflows?mode=continuous',
      restrictionKey: 'continuousWorkflowsList',
      dataTour: 'workflowsContinuousLink',
    },
    { name: 'Triggered', icon: IconBolt, to: '/workflows?mode=triggered', restrictionKey: 'triggeredWorkflowsList' },
    {
      name: 'AI proposals',
      icon: IconGitPullRequest,
      to: '/flowProposals',
      pendingCountPaths: ['flowProposals/count?status=pending', 'aiDrafts/count?type=workflow'],
    },
  ],
  channels: [
    { name: 'Emails', icon: IconMail, to: '/emails', restrictionKey: 'emailsList', dataTour: 'emailsLink' },
    { type: 'divider' },
    { name: 'Push Notifications', icon: IconDeviceMobile, to: '/push', restrictionKey: 'pushNotificationsList' },
    { name: 'Banners', icon: IconBrowserPlus, to: '/banners', restrictionKey: 'bannersList' },
    { type: 'divider' },
    { name: 'SMS / Text Messages', icon: IconMessage, to: '/textMessages', restrictionKey: 'textMessagesList' },
    {
      name: 'Viber Messages',
      icon: IconPhone,
      to: '/viber',
      restrictionKey: 'viberMessagesList',
      dataTour: 'viberLink',
    },
    { name: 'WhatsApp Messages', icon: IconBrandWhatsapp, to: '/whatsapp', restrictionKey: 'whatsappMessagesList' },
    { name: 'Webhooks', icon: IconWebhook, to: '/webhooks', restrictionKey: 'webhooksList' },
    { type: 'divider' },
    { name: 'Facebook Ads', icon: IconBrandFacebook, to: '/facebook/ads', restrictionKey: 'facebookAdsList' },
    { name: 'Google Ads', icon: IconBrandGoogle, to: '/google/ads', restrictionKey: 'googleAdsList' },
    {
      name: 'Spradz Influencers',
      icon: IconStar,
      to: '/influencers',
      restrictionKey: 'influencersList',
      countryFilter: 'Indonesia',
    },
  ],
  data: [
    {
      name: 'Segments',
      icon: IconUsersGroup,
      to: '/segments',
      restrictionKey: 'segmentsList',
      dataTour: 'segmentsLink',
    },
    {
      name: 'Sync Segments',
      icon: IconRefresh,
      to: '/syncSegments',
      restrictionKey: 'syncSegments',
      dataTour: 'syncSegments',
    },
    {
      name: 'Profiles',
      icon: IconUsers,
      to: '/profiles?showAnonymous=false',
      restrictionKey: 'profilesList',
      restrictedNavigation: true,
      dataTour: 'profilesLink',
    },
    {
      name: 'Companies',
      icon: IconBuildingStore,
      to: '/companies',
      restrictionKey: 'companiesList',
      restrictedNavigation: true,
      dataTour: 'companiesLink',
    },
    { name: 'Events', icon: IconMouse, to: '/events', restrictionKey: 'eventsList', restrictedNavigation: true },
    { type: 'divider' },
    {
      name: 'Products',
      icon: IconGift,
      to: '/products',
      restrictionKey: 'productsList',
      restrictedNavigation: true,
      dataTour: 'productsLink',
    },
    { name: 'Product Feeds', icon: IconPackages, to: '/productFeeds', restrictionKey: 'productFeedsList' },
    { name: 'Catalog Triggers', icon: IconSettings, to: '/catalogTriggers', restrictionKey: 'catalogTriggersList' },
    {
      name: 'Facebook Product Sets',
      icon: IconBrandFacebook,
      to: '/facebook/productSets',
      restrictionKey: 'facebookProductSetsList',
    },
    { type: 'divider' },
    { name: 'Surveys', icon: IconQuestionMark, to: '/surveys', restrictionKey: 'surveysList' },
    { name: 'NPS', icon: IconMessageCircle, to: '/nps', restrictionKey: 'npsList' },
    { name: 'Loyalty', icon: IconHeartPlus, to: '/loyalty', restrictionKey: 'loyaltyList' },
    { name: 'Pipelines', icon: IconPlugConnected, to: '/pipelines', restrictionKey: 'pipelinesList' },
    { name: 'Dynamic Coupons', icon: IconTicket, to: '/coupons', restrictionKey: 'couponsList' },
  ],
  assets: [
    { name: 'Files', icon: IconPhoto, to: '/assets', restrictionKey: 'imagesList' },
    { name: 'Layouts', icon: IconFileCode, to: '/templates', restrictionKey: 'layoutsList' },
    { name: 'Email Templates Gallery', icon: IconLayersLinked, to: '/stripoTemplates' },
  ],
  settings: [
    { name: 'Shop & Integration', icon: IconBuildingStore, to: '/settings/domain', dataTour: 'shopSettingsLink' },
    { name: 'Messaging', icon: IconSend, to: '/settings/messagingAndAds', dataTour: 'messagingSettingsLink' },
    { name: 'Facebook & Whatsapp', icon: IconBrandMeta, to: '/settings/meta' },
    { name: 'Google', icon: IconBrandGoogle, to: '/settings/google' },
    { name: 'AWS', icon: IconBrandAmazon, to: '/settings/aws' },
    { name: 'TikTok', icon: IconBrandTiktok, to: '/settings/tiktok' },
  ],
};

// Helper to determine active section from pathname
const getActiveSectionFromPath = (pathname) => {
  // Extract the subpath after /accounts/{id}/domains/{id}/
  const pathParts = pathname.split('/');
  const subpath = pathParts.slice(5).join('/'); // Everything after domains/{id}

  // Special handling for pages with recommenders/bannerBlocks in the URL
  // e.g., pages/137/recommenders/8/details or pages/137/bannerBlocks/8/details
  const pagesMatch = subpath.match(/^pages\/\d+\/(\w+)/);
  if (pagesMatch) {
    const urlSegment = pagesMatch[1]; // e.g., 'recommenders' or 'bannerBlocks'
    if (urlSegment === 'recommenders' || urlSegment === 'bannerBlocks' || urlSegment === 'postPurchases') {
      return 'personalization';
    }
  }

  // Check each section's links to find which section the current path belongs to
  for (const [sectionId, links] of Object.entries(sectionLinks)) {
    for (const link of links) {
      if (link.type === 'divider') continue;

      // Extract the path part from the link's 'to' property (without query params)
      const linkPath = link.to.split('?')[0].replace(/^\//, ''); // Remove leading slash

      // Check if the current subpath starts with or matches the link path
      if (subpath === linkPath || subpath.startsWith(linkPath + '/') || subpath.startsWith(linkPath)) {
        return sectionId;
      }
    }
  }

  return 'analytics'; // Default
};

const AppSidebar = () => {
  const location = useLocation();
  const { domain } = useSelector((state) => state.domain);
  const [activeSection, setActiveSection] = useState('analytics');
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [pendingCounts, setPendingCounts] = useState({});
  const fetch = useFetch();
  const countedLink = isPanelOpen ? sectionLinks[activeSection].find((link) => link.pendingCountPaths) : undefined;

  useEffect(() => {
    if (!countedLink) {
      return;
    }
    Promise.all(
      countedLink.pendingCountPaths.map((path) =>
        fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/${path}`)
          .then(({ count }) => count)
          .catch(() => 0)
      )
    ).then((counts) =>
      setPendingCounts((prev) => ({ ...prev, [countedLink.to]: counts.reduce((sum, count) => sum + count, 0) }))
    );
  }, [countedLink, fetch, domain.accountId, domain.id]);

  // Update active section based on current path
  useEffect(() => {
    const section = getActiveSectionFromPath(location.pathname);
    setActiveSection(section);
  }, [location.pathname]);

  // Handle icon click - open panel or switch section
  const handleIconClick = (sectionId) => {
    setActiveSection(sectionId);
    setIsPanelOpen(true);
  };

  // Handle icon hover - show panel for that section
  const handleIconHover = (sectionId) => {
    setActiveSection(sectionId);
    setIsPanelOpen(true);
  };

  // Render main navigation icons
  const mainLinks = mainSections.map((section) => (
    <Tooltip
      label={section.label}
      position="right"
      withArrow
      transitionProps={{ duration: 0 }}
      key={section.id}
      disabled={isPanelOpen}
    >
      <UnstyledButton
        onClick={() => handleIconClick(section.id)}
        onMouseEnter={() => handleIconHover(section.id)}
        className={classes.mainLink}
        data-active={section.id === activeSection || undefined}
        data-tour={section.dataTour}
      >
        <section.icon size={22} stroke={1.5} />
      </UnstyledButton>
    </Tooltip>
  ));

  // Get current section's links
  const currentLinks = sectionLinks[activeSection] || [];

  // Render links for the active section
  const links = currentLinks.map((link, index) => {
    // Handle dividers
    if (link.type === 'divider') {
      return <div key={`divider-${index}`} className={classes.divider} />;
    }

    // Filter by country if needed
    if (link.countryFilter && domain?.country !== link.countryFilter) {
      return null;
    }

    const IconComponent = link.icon;

    return (
      <LockableLinkContainer
        key={link.to}
        name={link.name}
        icon={<IconComponent size={18} />}
        to={link.to}
        restrictionKey={link.restrictionKey}
        restrictedNavigation={link.restrictedNavigation}
        data-tour={link.dataTour}
        className={classes.link}
        rightSection={pendingCounts[link.to] > 0 ? <Badge size="sm">{pendingCounts[link.to]}</Badge> : undefined}
      />
    );
  });

  // Get current section label
  const currentSectionLabel = mainSections.find((s) => s.id === activeSection)?.label || 'Navigation';

  // Close panel when clicking a link
  const handleLinkClick = () => {
    setIsPanelOpen(false);
  };

  // Close panel when mouse leaves the sidebar area
  const handleMouseLeave = () => {
    setIsPanelOpen(false);
  };

  return (
    <div className={classes.sidebarContainer} onMouseLeave={handleMouseLeave} data-tour="sidebarContainer">
      <nav className={classes.navbar}>
        <div className={classes.wrapper}>
          <div className={classes.aside}>{mainLinks}</div>
        </div>
      </nav>
      {isPanelOpen && (
        <div className={classes.main} onClick={handleLinkClick} data-tour="sidebarPanel">
          <Title order={5} className={classes.title}>
            {currentSectionLabel}
          </Title>
          <div className={classes.linksContainer}>{links}</div>
        </div>
      )}
    </div>
  );
};

export default AppSidebar;
